import React, { useState } from "react";
import { Button, Callout, Classes, Dialog, Intent, TextArea } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { EntityType } from "../../../../models/enums";
import { ISyncSchedule } from "../../../../models/SyncSchedule";
import styles from "./SyncSchedulePanel.module.scss";

interface IScheduleImpact {
  addedEntities: EntityType[];
  removedEntities: EntityType[];
  missingDependencies: Array<{ missingDep: EntityType; requiredBy: EntityType[] }>;
  hasIssues: boolean;
}

interface IConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (additionalNote: string) => void;
  saving: boolean;
  syncSchedules: ISyncSchedule[];
  pendingChangesBySchedule: { [scheduleIndex: number]: EntityType[] };
  originalEntitiesBySchedule: { [scheduleIndex: number]: EntityType[] };
  getScheduleLabel: (schedule: ISyncSchedule, index: number) => string;
  getDisplayName: (entityType: EntityType) => string;
  getScheduleImpactSummary: (scheduleIndex: number) => IScheduleImpact;
  requiresEgress: boolean;
}

const ConfirmationDialog: React.FC<IConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  saving,
  syncSchedules,
  pendingChangesBySchedule,
  originalEntitiesBySchedule,
  getScheduleLabel,
  getDisplayName,
  getScheduleImpactSummary,
  requiresEgress
}) => {
  const [additionalNote, setAdditionalNote] = useState("");

  const handleConfirm = () => {
    onConfirm(additionalNote);
    setAdditionalNote(""); // Clear after confirm
  };

  const handleClose = () => {
    setAdditionalNote(""); // Clear on close
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={saving ? undefined : handleClose}
      title="Confirm All Sync Schedule Changes"
      icon={IconNames.CONFIRM}
      style={{ width: '700px' }}
      canEscapeKeyClose={!saving}
      canOutsideClickClose={!saving}
    >
      <div className={`${Classes.DIALOG_BODY} ${styles.confirmDialogBody}`}>
        <div className={styles.confirmIntro}>
          You are about to save changes across multiple sync schedules:
        </div>

        {syncSchedules.map((schedule, index) => {
          const pending = pendingChangesBySchedule[index] || [];
          const original = originalEntitiesBySchedule[index] || [];
          const added = pending.filter(et => !original.includes(et));
          const removed = original.filter(et => !pending.includes(et));
          const scheduleImpact = getScheduleImpactSummary(index);
          
          if (added.length === 0 && removed.length === 0) return null;
          
          return (
            <div key={index} className={styles.confirmScheduleBlock}>
              <div className={styles.confirmScheduleTitle}>
                {getScheduleLabel(schedule, index)}
              </div>
              
              {added.length > 0 && (
                <div className={styles.confirmChangeSection}>
                  <div className={`${styles.confirmChangeSectionTitle} ${styles.confirmChangeSectionTitleAdding}`}>
                    ✓ Adding ({added.length}):
                  </div>
                  <ul className={styles.confirmChangeList}>
                    {added.map(et => (
                      <li key={et}>{getDisplayName(et)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {removed.length > 0 && (
                <div className={styles.confirmChangeSection}>
                  <div className={`${styles.confirmChangeSectionTitle} ${styles.confirmChangeSectionTitleRemoving}`}>
                    ✗ Removing ({removed.length}):
                  </div>
                  <ul className={styles.confirmChangeList}>
                    {removed.map(et => (
                      <li key={et}>{getDisplayName(et)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {requiresEgress && scheduleImpact.missingDependencies.length > 0 && (
                <Callout intent={Intent.WARNING} icon={IconNames.WARNING_SIGN}>
                  <div className={styles.confirmDependencyWarningTitle}>Missing Dependencies:</div>
                  {scheduleImpact.missingDependencies.map(({ missingDep, requiredBy }) => (
                    <div key={missingDep} className={styles.confirmDependencyItem}>
                      <strong>{getDisplayName(missingDep)}</strong> is required by:
                      <ul className={styles.confirmDependencyItemList}>
                        {requiredBy.map(entity => (
                          <li key={entity}>{getDisplayName(entity)}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </Callout>
              )}

              {requiresEgress && !scheduleImpact.hasIssues && (added.length > 0 || removed.length > 0) && (
                <Callout intent={Intent.SUCCESS} icon={IconNames.TICK}>
                  No dependency issues for this schedule.
                </Callout>
              )}
            </div>
          );
        })}

        <div className={styles.additionalNotesContainer}>
          <label htmlFor="additional-notes" className={styles.additionalNotesLabel}>
            Additional Notes (optional):
          </label>
          <TextArea
            id="additional-notes"
            value={additionalNote}
            onChange={(e) => setAdditionalNote(e.target.value)}
            placeholder="Add any additional notes about these changes..."
            fill
            rows={3}
            disabled={saving}
            style={{ resize: 'vertical' }}
          />
        </div>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            intent={Intent.PRIMARY}
            onClick={handleConfirm}
            icon={saving ? undefined : IconNames.FLOPPY_DISK}
            loading={saving}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Confirm & Save'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ConfirmationDialog;
